//@author: devin
import { HttpClient, HttpErrorResponse, HttpResponse, HttpXhrBackend } from "@angular/common/http";
import { lastValueFrom, Observable, Subject, takeUntil } from "rxjs";
import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { jSith } from "../util/jquery-replacement";
import { MaybeArray, Nullable } from "../pojo/Util";
import { NetworkHealthService } from "../services/network.health.service";

export class ResponseMessage<U = unknown> {
    message?: string;
    messageCode?: string;
    bulkInsertResponse?: BulkInsertResponse;
    upsertData?: U;
    /** Non-fatal messages from a successful workflow transition (for example, conflicts saved as preferences). */
    warnings?: string[];
}

export class UpsertResponseMessage<T> extends ResponseMessage<T[]> {
    declare upsertData?: T[];
}

export class BulkInsertResponse {
    outputs?: { [key: string]: string }[];
}

export interface HttpRequestOptions {
    data?: any;
    https?: boolean;
    headers?: Record<string, string>;
    responseType?: "arraybuffer" | "blob" | "text" | "json";
    curRetry?: number;
    observe?: "body" | "events" | "response";
    abortSignal?: AbortSignal;
    throwErrorClass?: boolean;
}

// TODO: make these types a little more sensible
export type HttpGetRequestOptions = Omit<HttpRequestOptions, "data">;
// omit data from POST options because `DataAccess.post` has an explicit data arg
export type HttpPostRequestOptions = Omit<HttpRequestOptions, "data" | "curRetry">;
export type HttpPutRequestOptions = HttpPostRequestOptions;
export type HttpDeleteRequestOptions = Pick<HttpRequestOptions, "data" | "headers">;
export type HttpPatchRequestOptions = HttpPostRequestOptions;

export class DataAccess {
    public static DataAccess_HEADERS: any = {
        contentType: {
            xml: { "Content-Type": "text/xml;charset=utf-8", Accept: "text/xml" },
            json: { "Content-Type": "application/json" },
        },
    };

    public static DataAccess_RETRY: any = {
        responseCodes: { 429: true, 0: true },
        maxRetry: 5,
        waitMs: 1000,
    };

    private static jwt = "";

    public static setJwt(jwt: string) {
        DataAccess.jwt = jwt;
    }

    public static hasJwt() {
        return !!DataAccess.jwt;
    }

    public static get<T = any>(serviceUrl: string, options?: HttpGetRequestOptions): Promise<T> {
        return DataAccess.http("get", serviceUrl, options);
    }

    public static post<T = any>(serviceUrl: string, data?: any, options?: HttpPostRequestOptions): Promise<T> {
        return DataAccess.http("post", serviceUrl, {
            data,
            ...(options ?? {}),
        });
    }

    public static put<T = any>(serviceUrl: string, data?: any, options?: HttpPutRequestOptions): Promise<T> {
        return DataAccess.http("put", serviceUrl, {
            data,
            ...(options ?? {}),
        });
    }

    public static patch<T = any>(serviceUrl: string, data?: any, options?: HttpPatchRequestOptions): Promise<T> {
        return DataAccess.http("patch", serviceUrl, {
            data,
            ...(options ?? {}),
        });
    }

    public static delete<T = any>(serviceUrl: string, options?: HttpDeleteRequestOptions): Promise<T> {
        return DataAccess.http("delete", serviceUrl, options);
    }

    public static postFile(serviceUrl: string, inputFile: any) {
        const formData = new FormData();
        formData.append("file", inputFile);
        return DataAccess.post(serviceUrl, formData, {
            headers: {},
        });
    }

    public static composeParam(name: string, value: string | number, noAmp?: boolean) {
        if (S25Util.isUndefined(name) || S25Util.isUndefined(value)) {
            return "";
        }
        return (noAmp ? "" : "&") + name + "=" + value;
    }

    public static composeParamUrlEncode(name: string, value: string | number, noAmp?: boolean) {
        if (S25Util.isUndefined(name) || S25Util.isUndefined(value)) {
            return "";
        }
        return DataAccess.composeParam(encodeURIComponent(name), encodeURIComponent(value), noAmp);
    }

    public static params(
        params: Record<string, MaybeArray<Nullable<string | number | boolean>>>,
        options?: { omitNullish?: boolean; sort?: boolean },
    ) {
        function getString(value: MaybeArray<Nullable<string | number | boolean>>): Nullable<string> {
            if (S25Util.array.isArray(value)) return value.map(getString).filter(S25Util.isDefined).join("+");
            if (!S25Util.isDefined(value)) return value;
            const valueStr = typeof value !== "string" ? String(value) : value;
            return valueStr;
        }

        const finalsParams: Record<string, string> = {};
        for (const [key, value] of Object.entries(params)) {
            if ((options?.omitNullish ?? true) && S25Util.isUndefined(params[key])) continue;
            const valueStr = getString(value);
            if (S25Util.isDefined(valueStr)) finalsParams[key] = valueStr;
        }

        const searchParams = new URLSearchParams(finalsParams);

        if (options?.sort ?? true) searchParams.sort();

        if (searchParams.size === 0) return "";
        return searchParams.toString();
    }

    public static injectCaller(url: string, caller?: string): string {
        caller = caller || "pro";

        if (!caller || !url) {
            return url;
        }

        // contains - prepend
        if (S25Util.contains(url, "caller=")) {
            return url.replace("caller=", "caller=" + caller + "-");
        }

        // incomplete URL - replace "?" with new param; s25ModalSubscribeController
        // http://bugs.collegenet.com/browse/ANG-1474
        if (url.endsWith("=") && S25Util.contains(url, "?")) {
            return url.replace("?", "?" + DataAccess.composeParam("caller", caller, true) + "&");
        }

        // append new param
        return url + (S25Util.contains(url, "?") ? "&" : "?") + DataAccess.composeParam("caller", caller, true);
    }

    public static composeUrl(serviceUrl: string, isHttps?: boolean): string {
        if (serviceUrl && serviceUrl.startsWith("http")) {
            return serviceUrl;
        }
        if (!S25Const.webservicesBaseUrl) console.error("baseUrl not set", serviceUrl); //helpful when test is making a real call
        let url: string = S25Const.webservicesBaseUrl.replace("http://", "http" + (isHttps ? "s" : "") + "://");
        url += serviceUrl;
        url = DataAccess.injectCaller(url, "pro");
        return url;
    }

    public static getUrl(serviceUrl: string) {
        return DataAccess.composeUrl(serviceUrl);
    }

    public static massageData(data: any, isXml?: boolean): any {
        //add r25 namespace to all xml requests, if not already present
        if (data && data.indexOf && data.replace && data.indexOf("xmlns:r25") === -1 && data.indexOf("r25:") > -1) {
            data = data.replace(/<(r25:.*?)>/, '<$1 xmlns:r25="http://www.collegenet.com/r25">');
        }

        //for debugging purposes, " is always preferred instead of " for XML attribute declarations (added on request from Nathan)
        if (data && data.replace && isXml) {
            data = S25Util.removeInvalidXmlCharacters(data);
            data = data.replace(/"/g, '"');
        }

        if (typeof data === "object" && data !== null) {
            S25Util.propertyDeleteStartsWith(data, "$");
            S25Util.removeInvalidXmlCharacters(data);

            // serialize dates before passing to WS
            S25Util.dfs(data, (node, parent, childKey) => {
                if (S25Util.date.isDate(node)) {
                    parent[childKey] = S25Util.date.toS25ISODateTimeStr(node);
                }
            });
        }

        return data;
    }

    public static isXmlRequest(url: string): boolean {
        return (
            url.indexOf(".xml?") !== -1 ||
            url.indexOf(".xml", url.length - 4) !== -1 ||
            url.indexOf(".copyto") !== -1 ||
            url.indexOf(".mail") !== -1 ||
            url.indexOf(".run") !== -1 ||
            url.indexOf("s25_exec") !== -1 ||
            url.indexOf(".shibboleth") !== -1
        );
    }

    public static isJsonRequest(url: string): boolean {
        return url.indexOf(".json") > -1 || url.indexOf(".generate") !== -1;
    }

    /**
     * Promise rejections will have `data` property with error info if it's available
     */
    public static http<T = any>(method: string, serviceUrl: string, options: HttpRequestOptions = {}): Promise<T> {
        if (window.unitTesting) {
            const which = window.currentSpec ? `The test "${window.currentSpec?.fullName}"` : "A test";
            const message = `Mock your stuff! ${which} attempted a ${method.toUpperCase()} to ${serviceUrl}`;
            console.error(message); // Also log message in case error is caught
            throw new Error(message);
        }
        const extCall = !!serviceUrl?.startsWith("http");
        const url: string = DataAccess.composeUrl(serviceUrl, options.https);
        const massagedData = DataAccess.massageData(options.data, DataAccess.isXmlRequest(serviceUrl));
        options.curRetry = S25Util.coalesce(options.curRetry, 0);

        options.headers ||= DataAccess.isXmlRequest(serviceUrl)
            ? DataAccess.DataAccess_HEADERS.contentType.xml
            : DataAccess.isJsonRequest(serviceUrl)
              ? DataAccess.DataAccess_HEADERS.contentType.json
              : undefined;

        if (DataAccess.isXmlRequest(serviceUrl)) {
            options.responseType = options.responseType || "text";
        }

        return S25Const.getPersistentSessionIdParam().then(async () => {
            if (DataAccess.jwt) {
                options.headers = options.headers || {};
                options.headers["Authorization"] = "Bearer " + DataAccess.jwt;
            } else if (S25Const.persistentSessionIdParam) {
                options.headers = options.headers || {};
                options.headers["X-SERIES25-SESSION_ID"] = S25Const.persistentSessionIdParam;
            }
            let request: Observable<T> = window.angBridge.HttpClient.request(method, url, {
                body: massagedData || "", //note: data of "" needed for angular $http to NOT strip content-type header...
                headers: options.headers,
                responseType: options.responseType,
                withCredentials: url.indexOf("lynx") > -1,
                observe: options.observe,
            });
            if (options.abortSignal) {
                // if it's ALREADY aborted by the time we got here...
                if (options.abortSignal.aborted) {
                    return Promise.reject(
                        new DOMException("Aborted", options.abortSignal.reason || "Unknown Abort Error"),
                    );
                }
                const abort$ = new Subject<void>();
                options.abortSignal.addEventListener("abort", () => abort$.next());
                request = request.pipe(takeUntil(abort$));
            }

            const requestStart = performance.now();
            return lastValueFrom(request, { defaultValue: undefined }).then(
                (resp) => {
                    if (S25Const.inApp) NetworkHealthService.recordRequestTiming(performance.now() - requestStart);
                    return resp;
                },
                (err: HttpErrorResponse & { data?: any }) => {
                    if (S25Const.inApp) NetworkHealthService.recordRequestTiming(performance.now() - requestStart);
                    //callers expect data property to have error info
                    if (err?.error) {
                        err.data ||= err.error;
                    }
                    // non-configured response codes - reject as-is
                    if (extCall || !DataAccess.DataAccess_RETRY.responseCodes[err.status]) {
                        throw options.throwErrorClass ? this.createErr(err) : err;
                    } else if (options.curRetry > DataAccess.DataAccess_RETRY.maxRetry) {
                        console.log(method + ": " + url);
                        console.log(
                            "status=" +
                                err.status +
                                ", rejecting" +
                                ", retry " +
                                options.curRetry +
                                " of " +
                                DataAccess.DataAccess_RETRY.maxRetry,
                        );
                        throw options.throwErrorClass ? this.createErr(err) : err;
                    } else {
                        return jSith.timeout(
                            () =>
                                DataAccess.http(method, serviceUrl, {
                                    ...options,
                                    curRetry: options.curRetry + 1,
                                }),
                            DataAccess.DataAccess_RETRY.waitMs,
                        );
                    }
                },
            );
        });
    }

    private static createErr(err: HttpErrorResponse & { data?: any }) {
        const error = new Error(err.message);
        (<any>error).data = err.data;
        return error;
    }
}
